import type { DataGridProProps } from '@mui/x-data-grid-pro';

import { useState, useCallback } from 'react';
import { usePopover } from 'minimal-shared/hooks';
import {
  EyeIcon,
  EyeCloseIcon,
  DensityCompactIcon,
  DensityStandardIcon,
  DensityComfortableIcon,
} from '../../theme-minimals/core/components/mui-x-data-grid';

import Menu from '@mui/material/Menu';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import { svgIconClasses } from '@mui/material/SvgIcon';

import { _Lucide as Lucide } from '../lucide';
import { Settings } from 'lucide-react';
import { IconButton } from '@mui/material';

// ----------------------------------------------------------------------

/**
 * Docs
 * https://mui.com/x/react-data-grid/components/toolbar/
 */

export type GridSettingsState = Pick<
  DataGridProProps,
  'density' | 'showCellVerticalBorder' | 'showColumnVerticalBorder'
>;

export type CustomToolbarSettingsButtonProps = {
  settings: GridSettingsState;
  onChangeSettings: React.Dispatch<React.SetStateAction<GridSettingsState>>;
};

export function useToolbarSettings(
  initialSettings?: Partial<GridSettingsState>,
): CustomToolbarSettingsButtonProps {
  const defaultSettings: GridSettingsState = {
    density: 'standard',
    showCellVerticalBorder: false,
    showColumnVerticalBorder: false,
    ...initialSettings,
  };

  const [settings, setSettings] = useState<GridSettingsState>(defaultSettings);

  return {
    settings,
    onChangeSettings: setSettings,
  };
}

const GRID_DENSITY_OPTIONS = [
  { value: 'compact', label: 'Compact', icon: <DensityCompactIcon /> },
  { value: 'standard', label: 'Standard', icon: <DensityStandardIcon /> },
  { value: 'comfortable', label: 'Comfortable', icon: <DensityComfortableIcon /> },
] as const;

// ----------------------------------------------------------------------

export function CustomToolbarSettingsButton({
  settings,
  onChangeSettings,
}: CustomToolbarSettingsButtonProps) {
  const { open, anchorEl, onClose, onOpen } = usePopover();

  const handleChangeDensity = useCallback(
    (value: GridSettingsState['density']) => {
      onChangeSettings((prev) => ({ ...prev, density: value }));
    },
    [onChangeSettings],
  );

  const handleToggleSetting = useCallback(
    (key: 'showCellVerticalBorder' | 'showColumnVerticalBorder') => {
      onChangeSettings((prev) => ({ ...prev, [key]: !prev[key] }));
    },
    [onChangeSettings],
  );

  const renderToggleOption = (
    menuItemLabel: string,
    key: 'showColumnVerticalBorder' | 'showCellVerticalBorder',
  ) => {
    const selected = settings[key];

    return (
      <MenuItem key={key} selected={selected} onClick={() => handleToggleSetting(key)}>
        {selected ? <EyeIcon /> : <EyeCloseIcon />}
        {menuItemLabel}
      </MenuItem>
    );
  };

  return (
    <>
      <Tooltip title="Settings">
        <IconButton
          id="settings-menu-trigger"
          aria-controls="settings-menu"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={onOpen}
        >
          <Lucide icon={Settings} size={18} />
        </IconButton>
      </Tooltip>

      <Menu
        id="settings-menu"
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: {
            'aria-labelledby': 'settings-menu-trigger',
            sx: {
              [`& .${svgIconClasses.root}`]: {
                mr: 2,
                fontSize: 20,
              },
            },
          },
        }}
      >
        {GRID_DENSITY_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={settings.density === option.value}
            onClick={() => handleChangeDensity(option.value)}
          >
            {option.icon}
            {option.label}
          </MenuItem>
        ))}

        <Divider />

        {renderToggleOption('Show column borders', 'showColumnVerticalBorder')}
        {renderToggleOption('Show cell borders', 'showCellVerticalBorder')}
      </Menu>
    </>
  );
}
